import { useCallback, useEffect, useState } from "react";
import { api } from "../api.ts";
import type { MentionUser } from "../utils/messageDisplay.ts";
import type { SupportTask } from "../types.ts";

function dueLabel(due: string | null | undefined): string {
  if (!due) return "期限なし";
  const d = new Date(due);
  const over = d.getTime() < Date.now();
  return (over ? "⚠ " : "") + d.toLocaleString();
}

export function TasksPanel({
  ticketId,
  mentionUsers,
  canWrite,
  onChanged,
}: {
  ticketId: number;
  mentionUsers: MentionUser[];
  canWrite: boolean;
  onChanged?: (result?: SupportTask) => void;
}) {
  const [tasks, setTasks] = useState<SupportTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [dueLocal, setDueLocal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api<SupportTask[]>(`/support/tickets/${ticketId}/tasks?status=open`);
      setTasks(rows);
      setErr("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const created = await api<SupportTask>(`/support/tasks`, {
        method: "POST",
        body: JSON.stringify({
          ticket_id: ticketId,
          title: title.trim(),
          assignee: assignee.trim() || null,
          due_at: dueLocal ? new Date(dueLocal).toISOString() : null,
        }),
      });
      setTitle("");
      setDueLocal("");
      await load();
      onChanged?.(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const complete = async (id: number) => {
    try {
      const updated = await api<SupportTask>(`/support/tasks/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "done" }),
      });
      await load();
      onChanged?.(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="support-tasks-panel">
      {loading ? (
        <div className="support-tasks-panel__hint">読み込み中…</div>
      ) : tasks.length === 0 ? (
        <div className="support-tasks-panel__hint">未完了タスクはありません</div>
      ) : (
        <ul className="support-tasks-panel__list">
          {tasks.map((t) => (
            <li key={t.id} className="support-tasks-panel__item">
              <div className="support-tasks-panel__title">{t.title}</div>
              <div className="support-tasks-panel__meta">
                {t.assignee ? `@${t.assignee.split("@")[0]}` : "担当未設定"}
                {" · "}
                {dueLabel(t.due_at)}
              </div>
              {canWrite && (
                <button type="button" className="support-tasks-panel__done" onClick={() => void complete(t.id)}>
                  完了
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canWrite && (
        <div className="support-tasks-panel__form">
          <input
            className="support-tasks-panel__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="タスク（例: 工場に判断確認）"
          />
          <select className="support-tasks-panel__select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">担当（任意）</option>
            {mentionUsers.map((u) => (
              <option key={u.email} value={u.email}>{u.label || u.email}</option>
            ))}
          </select>
          <input
            type="datetime-local"
            className="support-tasks-panel__input"
            value={dueLocal}
            onChange={(e) => setDueLocal(e.target.value)}
            title="期限（任意）"
          />
          <button type="button" className="support-tasks-panel__add" disabled={!title.trim() || busy} onClick={() => void create()}>
            追加
          </button>
        </div>
      )}
      {err && <div className="support-tasks-panel__err">{err}</div>}
    </div>
  );
}
